import { debounce } from '@mui/material';
import { ConversationParticipantEntity } from '@provision/conversation-types';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { identity, isNullish } from 'remeda';
import { FormAutocomplete, FormTextField } from 'src/components';
import { SEARCH_DEBOUNCE_DELAY } from 'src/constants/delays';
import {
  ConversationType,
  conversationTypeOptions,
  PreparedConversationParticipantOption,
  useConversationAssigneeGroupsOptions,
  useConversationParticipantOptions,
  useConversationUrgencyOptions,
  usePartnersOptions,
} from 'src/graphql';
import { useSearch } from 'src/graphql/entities/search/use-search';
import { ConditionInput, EntityNames } from 'src/graphql/types';
import { RequesterListItem } from 'src/pages/Conversations/SidePanel/ConversationDetails/requester-list-item';
import { useGlobalStore } from 'src/store';
import { useScrollPaginateHandler } from 'src/utils/hooks';
import { sortAlphabetically } from 'src/utils/sorting';

export enum ConversationFormFields {
  Partner = 'partner',
  Group = 'group',
  Requester = 'requester',
  Site = 'address',
  ConversationType = 'conversationType',
  Urgency = 'urgency',
  Subject = 'subject',
}

export type ConversationPartner = {
  id: number;
  label?: string;
  supportEmail?: string;
  membershipEmail?: string;
};

export type ConversationRequester = {
  id: string;
  label: string;
  participantEntity: ConversationParticipantEntity;
  email?: string;
  data: unknown;
};

export interface NewConversationFormValues {
  [ConversationFormFields.Partner]: ConversationPartner | null;
  [ConversationFormFields.Group]: { id: string; label: string } | null;
  [ConversationFormFields.Requester]: ConversationRequester | null;
  [ConversationFormFields.Site]: { id: string; label: string } | null;
  [ConversationFormFields.ConversationType]: { id: ConversationType; label: string } | null;
  [ConversationFormFields.Urgency]: { id: string; label: string } | null;
  [ConversationFormFields.Subject]: string;
}

const SUBJECT_MAX_LENGTH = 100;
const LIST_MAX_HEIGHT = '18rem';

export function useNewConversationForm({
  formMethods,
  partner,
  onCreateClient,
  onCreateSite,
}: {
  formMethods: ReturnType<typeof useForm<NewConversationFormValues>>;
  partner: ConversationPartner | null;
  onCreateClient: (partnerId: number) => void;
  onCreateSite: (requester: PreparedConversationParticipantOption) => void;
}) {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const partnerOptions = usePartnersOptions();
  const { control, setValue } = formMethods;
  const requester = useWatch({ control, name: ConversationFormFields.Requester });

  const conversationAssigneeGroupOptions = useConversationAssigneeGroupsOptions(partner?.id);
  const conversationUrgencyOptions = useConversationUrgencyOptions(partner?.id);

  const [searchRequesterTerm, setSearchRequesterTerm] = useState('');
  const [searchAddressTerm, setSearchAddressTerm] = useState('');

  const {
    getConversationParticipantOptions,
    conversationParticipantOptions,
    loading: requesterLoading,
  } = useConversationParticipantOptions();

  const requesterOptions = useMemo(
    () =>
      conversationParticipantOptions
        .filter((it) =>
          [
            ConversationParticipantEntity.Client,
            ConversationParticipantEntity.PlatformUser,
          ].includes(it.participantEntity),
        )
        .map((option) => ({ ...option, data: option, label: option.label || option.email })),
    [conversationParticipantOptions],
  );

  useEffect(() => {
    if (partner?.id) {
      getConversationParticipantOptions(
        { partnerId: partner.id, searchText: searchRequesterTerm },
        true,
      );
    }
  }, [partner?.id, searchRequesterTerm, getConversationParticipantOptions]);

  const addressFilters = useMemo(() => {
    const filters: ConditionInput[] = [];
    if (!isNullish(partner?.id)) {
      filters.push({ attribute: 'partnerId', operator: '=', value: String(partner.id) });
    }
    if (requester?.id) {
      filters.push({ attribute: 'clients/clientId', operator: '=', value: requester.id });
    }
    return filters;
  }, [partner?.id, requester?.id]);

  const {
    records: addresses,
    isLoading: addressLoading,
    loadMore: addressLoadMore,
    hasMore: addressHasMore,
  } = useSearch({
    entityName: EntityNames.Site,
    searchTerm: searchAddressTerm,
    filter: addressFilters,
    sort: { attribute: 'address1', desc: false },
    showResultsWhenEmptySearchTerm: true,
    skip: isNullish(requester?.id),
  }) ?? {};

  const addressHandleScroll = useScrollPaginateHandler({
    loadMore: addressLoadMore,
    enabled: addressHasMore,
    thresholdPx: 100,
  });

  const addressOptions = useMemo(() => {
    if (!addresses) return [];
    return (
      addresses
        // Remove duplicated elements
        ?.filter(
          (address, index, arr) =>
            arr.findIndex((a) => a.addressId === address.addressId) === index,
        )
        .map((address) => ({
          id: address.addressId,
          label: address.fullAddress ?? '',
        }))
        .sort(sortAlphabetically('label'))
    );
  }, [addresses]);

  const searchRequester = useMemo(
    () =>
      debounce(({ target }: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setSearchRequesterTerm(target.value);
      }, SEARCH_DEBOUNCE_DELAY),
    [],
  );

  const searchAddress = useMemo(
    () =>
      debounce(({ target }: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setSearchAddressTerm(target.value);
      }, SEARCH_DEBOUNCE_DELAY),
    [],
  );

  const clearSearches = () => {
    setSearchRequesterTerm('');
    setSearchAddressTerm('');
  };

  const showPartnerField = partnerOptions.length > 1 && isNullish(selectedPartnerId);

  const formFields = (
    <>
      {showPartnerField ? (
        <FormAutocomplete
          type="autocomplete"
          label="Partner"
          options={partnerOptions}
          name={ConversationFormFields.Partner}
          required="Partner is required"
          showErrorText
          blurOnSelect={false}
          data-testid="create-conversation-partner"
          autocompleteProps={{
            onChange: () => {
              clearSearches();
              setValue(ConversationFormFields.Group, null);
              setValue(ConversationFormFields.Requester, null);
              setValue(ConversationFormFields.Site, null);
              setValue(ConversationFormFields.ConversationType, null);
              setValue(ConversationFormFields.Urgency, null);
            },
          }}
        />
      ) : null}
      {!!partner && (
        <FormAutocomplete
          type="autocomplete"
          label="Group"
          options={conversationAssigneeGroupOptions}
          name={ConversationFormFields.Group}
          required="Group is required"
          showErrorText
          blurOnSelect={false}
          data-testid="create-conversation-group"
        />
      )}
      {!!partner && (
        <FormAutocomplete
          type="autocomplete"
          name={ConversationFormFields.Requester}
          label="Requester"
          required="Requester is required"
          showErrorText
          options={requesterOptions}
          creatable
          loading={requesterLoading}
          onAddNewItemLabel="Create Client"
          blurOnSelect={false}
          onAddNewItem={() => onCreateClient(partner.id)}
          data-testid="create-conversation-requester"
          autocompleteProps={{
            onChange: () => {
              clearSearches();
              setValue(ConversationFormFields.Site, null);
            },
            groupBy: (option) => option.group,
            loading: requesterLoading,
            ListboxProps: { sx: { maxHeight: LIST_MAX_HEIGHT } },
            componentsProps: {
              paper: {
                sx: {
                  // makes the group labels to have rounded corners too
                  overflow: 'hidden',
                },
              },
            },
            filterOptions: identity,
            renderOption: (props, option: PreparedConversationParticipantOption, state) => (
              <RequesterListItem
                id={option.id}
                props={props}
                key={option.id}
                inputValue={state.inputValue}
                firstLine={option.label}
                secondLine={option.email}
                participantEntity={option.participantEntity}
                externalPartnerId={
                  option.entityPartnerId !== partner.id ? option.entityPartnerId : undefined
                }
              />
            ),
          }}
          textFieldProps={{ onChange: searchRequester }}
        />
      )}
      {!!partner && requester?.participantEntity === ConversationParticipantEntity.Client && (
        <FormAutocomplete
          type="autocomplete"
          creatable
          name={ConversationFormFields.Site}
          label="Site"
          options={addressOptions}
          blurOnSelect={false}
          onAddNewItem={() =>
            onCreateSite(requester.data as PreparedConversationParticipantOption)
          }
          onAddNewItemLabel="Create New Site"
          data-testid="create-conversation-site"
          autocompleteProps={{
            loading: addressLoading,
            ListboxProps: {
              onScroll: addressHandleScroll,
              sx: { maxHeight: LIST_MAX_HEIGHT },
            },
            filterOptions: identity,
            onClose: () => setSearchAddressTerm(''),
          }}
          textFieldProps={{ onChange: searchAddress }}
        />
      )}
      <FormAutocomplete
        type="autocomplete"
        name={ConversationFormFields.ConversationType}
        label="Type"
        required="Type is required"
        showErrorText
        options={conversationTypeOptions}
        blurOnSelect={false}
        data-testid="create-conversation-type"
      />
      <FormAutocomplete
        type="autocomplete"
        name={ConversationFormFields.Urgency}
        label="Urgency"
        required="Urgency is required"
        showErrorText
        options={conversationUrgencyOptions}
        blurOnSelect={false}
        data-testid="create-conversation-urgency"
      />
      <FormTextField
        type="textfield"
        name={ConversationFormFields.Subject}
        label="Subject"
        required="Subject is required"
        showErrorText
        data-testid="create-conversation-subject"
        textFieldProps={{ inputProps: { maxLength: SUBJECT_MAX_LENGTH } }}
      />
    </>
  );

  return { formFields };
}
